import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface EscalationContactInfo {
  contactId: string | null;
  contactName: string | null;
}

export type EscalationContactsMap = Record<string, EscalationContactInfo>;

const CHUNK_SIZE = 100;

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * Para uma lista de session_ids (de tasks de Escalação CS), busca o contato
 * vinculado à sessão de atendimento. Retorna um mapa session_id -> contato.
 *
 * Faz chunking de 100 ids por request para evitar estouro de URL no PostgREST
 * (regra do projeto: evitar .in() com arrays grandes).
 */
export function useEscalationContacts(sessionIds: (string | null | undefined)[]) {
  const ids = Array.from(
    new Set(sessionIds.filter((id): id is string => !!id))
  ).sort();

  const key = ids.join(',');

  const { data } = useQuery({
    queryKey: ['escalation-contacts', key],
    queryFn: async (): Promise<EscalationContactsMap> => {
      if (ids.length === 0) return {};

      const chunks = chunk(ids, CHUNK_SIZE);
      const results = await Promise.all(
        chunks.map(async (c) => {
          const { data, error } = await supabase
            .from('support_sessions')
            .select('id, contact:contacts!support_sessions_contact_id_fkey(id, first_name, last_name)')
            .in('id', c);
          if (error) {
            console.error('[useEscalationContacts] chunk error:', error);
            return [];
          }
          return data ?? [];
        })
      );

      const map: EscalationContactsMap = {};
      for (const row of results.flat() as any[]) {
        const contact = row.contact;
        const name = contact
          ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || null
          : null;
        map[row.id] = {
          contactId: contact?.id ?? null,
          contactName: name,
        };
      }
      return map;
    },
    enabled: ids.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  return data ?? {};
}
